<template>
  <div class="rb-ob-form rb-ob-form--wide rb-match-page">
    <header v-if="phase !== 'processing'">
      <div class="rb-ob-progress" :aria-label="$t('intake.progressLabel')">
        <div class="rb-ob-progress-meta">
          <span>{{ progressMeta(9) }}</span>
          <span>{{ $t('intake.stepLabels.checkout') }}</span>
        </div>
        <div class="rb-ob-progress-bar" aria-hidden="true">
          <i :style="{ '--p': progressPercent(9) }"></i>
        </div>
      </div>
      <h1 class="rb-ob-title">
        {{ phase === 'success' ? $t('intake.checkoutSuccessTitle') : $t('intake.checkoutTitle') }}
      </h1>
    </header>

    <div
        v-if="phase === 'processing'"
        class="rb-payment-processing"
        role="status"
        aria-live="polite"
    >
      <span class="rb-checkout-spinner" aria-hidden="true"></span>
      <h2 class="rb-ob-title">{{ $t('intake.checkoutProcessingTitle') }}</h2>
      <p class="rb-ob-q-hint">{{ $t('intake.checkoutProcessingBody') }}</p>
    </div>

    <div v-else-if="phase === 'success'" class="rb-success-panel">
      <span class="rb-success-mark" aria-hidden="true">✓</span>
      <h2>{{ $t('intake.checkoutBookedTitle') }}</h2>
      <p>{{ successSummary }}</p>
      <div class="rb-receipt-line">
        <span>{{ $t('intake.checkoutBookingCode') }}</span>
        <b><bdi>{{ bookingCode }}</bdi></b>
      </div>
      <div class="rb-ob-actions rb-success-actions">
        <button class="rb-ob-submit" type="button" @click="onPrepare">
          {{ $t('intake.checkoutPrepare') }}
        </button>
        <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="goDashboard">
          {{ $t('intake.checkoutDashboard') }}
        </button>
      </div>
    </div>

    <div v-else class="rb-checkout-layout">
      <div class="rb-checkout-main">
        <div v-if="holdReady && holdSeconds > 0" class="rb-hold-notice" role="status">
          <b>{{ $t('intake.checkoutHoldTitle') }}</b>
          <span dir="ltr">{{ holdLabel }}</span>
        </div>
        <p v-else role="status">{{ formStatus || $t('intake.bookingUi.preparingHold') }}</p>
        <button v-if="!bookingGuard && !holdReady && !submitting && paymentInitiationEnabled" class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="beginCheckoutHold">{{ $t('intake.bookingUi.retry') }}</button>
        <router-link v-if="bookingGuard" class="rb-ob-submit rb-ob-submit-secondary" :to="bookingGuard.route">{{ bookingGuard.label }}</router-link>

        <section class="rb-payment-card">
          <h2>{{ $t('intake.checkoutPayTitle') }}</h2>
          <p>{{ $t('intake.checkoutPayIntro') }}</p>
          <form novalidate @submit.prevent="pay">
            <div
                class="rb-ob-field-block"
                :class="{ 'is-invalid': !!paymentError }"
            >
              <label
                  class="rb-pay-choice"
                  :class="{ 'is-selected': paymentMethod === 'CARD' }"
              >
                <input
                    v-model="paymentMethod"
                    type="radio"
                    name="paymentMethod"
                    value="CARD"
                    @change="paymentError = ''; formStatus = ''"
                >
                <span>{{ $t('intake.checkoutPayCard') }}</span>
              </label>
              <p class="rb-ob-err" :class="{ 'is-visible': !!paymentError }" role="alert">
                {{ paymentError || $t('intake.fieldRequired') }}
              </p>
            </div>
            <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
            <button
                class="rb-ob-submit"
                type="submit"
                :disabled="submitting || !!bookingGuard || !paymentPolicyLoaded || !paymentInitiationEnabled || !holdReady || holdSeconds <= 0"
                :class="{ 'is-loading': submitting }"
            >
              {{ payButtonLabel }}
            </button>
            <p v-if="paymentPolicyLoaded && !paymentInitiationEnabled" class="rb-ob-timezone-note">
              {{ $t('intake.bookingUi.paymentUnavailable') }}
            </p>
          </form>
        </section>
      </div>

      <aside v-if="person" class="rb-order-card">
        <h2>{{ $t('intake.checkoutOrderTitle') }}</h2>
        <div class="rb-order-person">
          <span class="rb-avatar" aria-hidden="true">{{ person.initial }}</span>
          <div>
            <b>{{ therapistDisplayName }}</b>
            <span>{{ therapistDisplayTitle }}</span>
          </div>
        </div>
        <dl>
          <div><dt>{{ $t('intake.holdSummaryTz') }}</dt><dd>{{ timezone }}</dd></div>
          <div>
            <dt>{{ $t('intake.checkoutOrderDate') }}</dt>
            <dd>{{ dateLabel }}</dd>
          </div>
          <div>
            <dt>{{ $t('intake.checkoutOrderTime') }}</dt>
            <dd><bdi>{{ timeLabel }}</bdi></dd>
          </div>
          <div>
            <dt>{{ $t('intake.checkoutOrderSession') }}</dt>
            <dd>{{ checkoutSessionLabel }}</dd>
          </div>
          <div>
            <dt>{{ $t('intake.sessionPrice') }}</dt>
            <dd>{{ displaySessionPrice }} {{ $t('intake.slotsPriceSuffix') }}</dd>
          </div>
          <div class="rb-order-total">
            <dt>{{ $t('intake.checkoutOrderAmount') }}</dt>
            <dd>{{ displayPayableAmount }}</dd>
          </div>
        </dl>
      </aside>
    </div>

    <div v-if="phase === 'checkout'" class="rb-ob-trust">
      <p>{{ $t('intake.trustPath') }}</p>
      <p>{{ $t('authUi.familyNote') }}</p>
    </div>
  </div>
</template>

<script>
import intakeFormMixin from '@/intake/intakeFormMixin'
import {
  loadIntake,
  saveIntake,
  hasSafetyRisk,
  isIntakeReadyToMatch,
  toFaDigits,
} from '@/intake/intakeState'
import {formatMoneyForDisplay, priceTierLabel} from '@/intake/formatMoney'
import {formatSlotDateTime, displayTimeZone} from '@/intake/formatSlotDateTime'
import {
  getMockMatch,
  getMockBookingDate,
} from '@/intake/mockMatches'
import {
  checkoutHold,
  getActiveBooking,
  getHold,
  holdBooking,
  isMatchmakingV2ApiEnabled,
  isSandboxMatchEnabled,
  releaseHold,
  shouldUseMockCheckout,
} from '@/intake/sandboxMatchApi'
import {
  getSessionPerson,
  hasSandboxMatchSession,
  loadMatchSession,
  saveMatchSession,
} from '@/intake/sandboxMatchSession'
import {fetchBookingOperations} from '@/intake/portalApi'
import {holdDeadline, holdRemaining} from '@/intake/holdState'
import {redirectToPayment} from '@/intake/paymentNavigation'
import {clientMessage, clientError} from '@/intake/clientMessages'
import {bookingGuardRecovery} from '@/intake/bookingGuardRecovery'

function newHoldIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return 'hold_' + crypto.randomUUID().replace(/-/g, '')
  }
  return 'hold_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10)
}

export default {
  name: 'MatchCheckoutStep',
  mixins: [intakeFormMixin],
  data() {
    const draft = loadIntake()
    return {
      phase: 'checkout',
      timezone: displayTimeZone(),
      person: null,
      bookingDate: draft.bookingDate || '',
      bookingTime: draft.bookingTime || '',
      paymentMethod: draft.paymentMethod || 'CARD',
      bookingCode: '',
      holdSeconds: 0,
      holdDeadlineAt: null,
      holdTimer: null,
      payTimer: null,
      submitting: false,
      formStatus: '',
      bookingGuard: null,
      paymentError: '',
      holdId: null,
      bookingId: null,
      holdReady: false,
      acceptedAmount: null,
      acceptedCurrency: null,
      paymentPolicyLoaded: false,
      paymentInitiationEnabled: false,
      paymentPolicyBlocker: 'XBLK-PAY-01',
    }
  },
  computed: {
    checkoutSessionLabel() {
      const session = loadMatchSession() || {}
      const minutes = (Date.parse(session.selectedSlotEndsAt) - Date.parse(session.selectedSlotStartsAt)) / 60000
      if (!Number.isFinite(minutes) || minutes <= 0) return this.$t('intake.checkoutSessionOnline')
      return this.$t('intake.checkoutSessionValue', {minutes: this.$route.params.lang === 'en' ? String(Math.round(minutes)) : toFaDigits(Math.round(minutes))})
    },
    useLiveBooking() {
      return isMatchmakingV2ApiEnabled()
        && hasSandboxMatchSession()
        && !shouldUseMockCheckout()
    },
    holdLabel() {
      const m = Math.floor(this.holdSeconds / 60)
      const s = this.holdSeconds % 60
      const raw = String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0')
      return this.toFaDigits(raw)
    },
    dateLabel() {
      const lang = this.$i18n.locale === 'en' ? 'en' : 'fa'
      if (isSandboxMatchEnabled() && hasSandboxMatchSession()) {
        const session = loadMatchSession()
        if (session.selectedSlotDateLabel) {
          return session.selectedSlotDateLabel
        }
        const iso = this.bookingTime || session.selectedSlotLabel || ''
        const formatted = formatSlotDateTime(iso, lang)
        if (formatted.dateLabel) {
          return formatted.dateLabel
        }
        return session.selectedSlotLabel || this.bookingDate || this.$t('intake.reviewEmpty')
      }
      const item = getMockBookingDate(this.bookingDate)
      if (!item) {
        return this.$t('intake.reviewEmpty')
      }
      return this.$i18n.locale === 'en' ? item.en : item.fa
    },
    timeLabel() {
      const lang = this.$i18n.locale === 'en' ? 'en' : 'fa'
      if (isSandboxMatchEnabled() && hasSandboxMatchSession()) {
        const session = loadMatchSession()
        if (session.selectedSlotTimeLabel) {
          return session.selectedSlotTimeLabel
        }
        const formatted = formatSlotDateTime(this.bookingTime, lang)
        if (formatted.timeLabel) {
          return formatted.timeLabel
        }
      }
      // Mock path stores HH:mm; live path should already be formatted above.
      if (this.bookingTime && !String(this.bookingTime).includes('T')) {
        return this.toFaDigits(this.bookingTime)
      }
      const formatted = formatSlotDateTime(this.bookingTime, lang)
      return formatted.timeLabel || this.toFaDigits(this.bookingTime)
    },
    displaySessionPrice() {
      if (!this.person) {
        return ''
      }
      return this.formatMoneyLabel(this.person.price, this.person.currency)
    },
    displayPayableAmount() {
      if (this.acceptedAmount != null) {
        return this.formatMoneyLabel(this.acceptedAmount, this.acceptedCurrency || (this.person && this.person.currency))
      }
      return this.displaySessionPrice + ' ' + this.$t('intake.slotsPriceSuffix')
    },
    payButtonLabel() {
      if (!this.person) {
        return this.$t('intake.checkoutPayAction')
      }
      const money = this.acceptedAmount != null
        ? formatMoneyForDisplay(this.acceptedAmount, this.acceptedCurrency || this.person.currency)
        : formatMoneyForDisplay(this.person.price, this.person.currency)
      const currency = (money.isToman || money.unit === 'raw')
        ? this.$t('intake.toman')
        : (money.currency || this.$t('intake.toman'))
      return this.$t('intake.checkoutPayActionAmount', {
        amount: money.text,
        currency,
      })
    },
    therapistDisplayName() {
      if (!this.person) {
        return ''
      }
      if (this.person.name) {
        return this.person.name
      }
      return this.$t('intake.therapist.' + this.person.id + '.name')
    },
    therapistDisplayTitle() {
      if (!this.person) {
        return ''
      }
      if (this.person.title && !/^tier_\d+$/i.test(this.person.title)) {
        return this.person.title
      }
      if (this.person.tier) {
        return priceTierLabel(this.person.tier, this.$route.params.lang || 'fa')
      }
      // Mock therapists only (t1/t2/t3); never invent live therapist id keys.
      if (this.person.sandbox && /^t\d+$/i.test(String(this.person.id))) {
        return this.$t('intake.therapist.' + this.person.id + '.title')
      }
      return ''
    },
    successSummary() {
      if (!this.person) {
        return ''
      }
      return this.$t('intake.checkoutBookedSummary', {
        date: this.dateLabel,
        time: this.timeLabel,
        name: this.therapistDisplayName,
      })
    },
  },
  created() {
    const draft = loadIntake()
    // V2 screening match: session people are authoritative — do not bounce to legacy draft gates.
    const hasV2Match = isSandboxMatchEnabled() && hasSandboxMatchSession()
    if (!hasV2Match) {
      if (!isIntakeReadyToMatch(draft)) {
        this.$router.replace({name: 'intakeScreening'})
        return
      }
      if (hasSafetyRisk(draft.safety)) {
        this.$router.replace({name: 'intakeCrisis'})
        return
      }
    }
    if (!draft.therapistId || !draft.bookingDate || !draft.bookingTime) {
      if (draft.therapistId) {
        this.$router.replace({
          name: 'intakeSlots',
          params: {
            lang: this.$route.params.lang || 'fa',
            therapistId: draft.therapistId,
          },
        })
      } else {
        this.$router.replace({name: 'intakeMatches'})
      }
      return
    }

    if (hasV2Match) {
      this.person = getSessionPerson(draft.therapistId)
      const session = loadMatchSession() || {}
      if (session.acceptedAmount != null) {
        this.acceptedAmount = session.acceptedAmount
        this.acceptedCurrency = session.acceptedCurrency || null
      }
    } else {
      this.person = getMockMatch(draft.therapistId)
    }
    if (!this.person) {
      this.$router.replace({name: 'intakeMatches'})
      return
    }

    this.loadPaymentPolicy()
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.refreshHoldCountdown)
    this.clearTimers()
  },
  mounted() { document.addEventListener('visibilitychange', this.refreshHoldCountdown) },
  methods: {
    progressPercent() { return '100%' },
    toFaDigits,
    formatMoneyLabel(amount, currency) {
      const money = formatMoneyForDisplay(amount, currency)
      if (money.isToman || money.unit === 'raw') {
        return money.text + ' ' + this.$t('intake.toman')
      }
      if (money.currency) {
        return money.text + ' ' + money.currency
      }
      return money.text
    },
    progressMeta() {
      return this.$t('intake.progressMeta', {
        step: this.toFaDigits(3),
        total: this.toFaDigits(3),
      })
    },
    clearTimers() {
      if (this.holdTimer) {
        clearInterval(this.holdTimer)
        this.holdTimer = null
      }
      if (this.payTimer) {
        clearTimeout(this.payTimer)
        this.payTimer = null
      }
    },
    goHoldExpired() {
      const lang = this.$route.params.lang || 'fa'
      this.$router.replace({name: 'intakeHoldExpired', params: {lang}})
    },
    abandonHold() {
      if (!this.useLiveBooking || !this.holdId) {
        return Promise.resolve()
      }
      return releaseHold(this.holdId).catch(() => null)
    },
    loadPaymentPolicy() {
      this.paymentPolicyLoaded = false
      return fetchBookingOperations().then((data) => {
        const payment = data && data.policy && data.policy.payment ? data.policy.payment : {}
        const productionOk = payment.production_initiation_enabled === true && payment.status !== 'BLOCKED'
        const localSim = payment.local_simulation_enabled === true
        this.paymentInitiationEnabled = productionOk || localSim
        this.paymentPolicyBlocker = payment.blocker_code || 'XBLK-PAY-01'
        if (!this.paymentInitiationEnabled) {
          this.holdReady = false
          this.formStatus = this.$t('intake.bookingUi.paymentUnavailable')
          return
        }
        if (!this.useLiveBooking) {
          this.holdReady = false
          this.formStatus = this.$t('intake.bookingUi.paymentUnavailable')
          return
        }
        return this.beginCheckoutHold()
      }).catch(() => {
        this.paymentInitiationEnabled = false
        this.holdReady = false
        this.formStatus = this.$t('intake.bookingUi.paymentUnavailable')
      }).finally(() => {
        this.paymentPolicyLoaded = true
      })
    },
    handleBookingGuard(error) {
      const recovery = bookingGuardRecovery(error, this.$route.params.lang || 'fa')
      if (!recovery) return false
      this.bookingGuard = recovery
      this.formStatus = recovery.message
      this.holdReady = false
      this.submitting = false
      this.clearTimers()
      return true
    },
    beginCheckoutHold() {
      if (this.submitting || this.bookingGuard) return Promise.resolve()
      if (!this.useLiveBooking) {
        this.holdReady = false
        this.formStatus = this.$t('intake.bookingUi.paymentUnavailable')
        return Promise.resolve()
      }
      const session = loadMatchSession() || {}
      this.submitting = true
      this.formStatus = ''
      const resume = this.resumeActiveCheckout(session).then((resumed) => {
        if (resumed || !session.holdId) return resumed
        return getHold(session.holdId).then((hold) => {
          if (!this.matchesCheckoutContext(hold, session, true)) throw new Error('HOLD_RECOVERY_FAILED')
          this.applyHoldPayload(hold, session.holdIdempotencyKey)
          return true
        })
      })
      return resume.then((resumed) => {
        if (resumed) return
        if (!session.validationId) {
          this.formStatus = this.$t('intake.checkoutHoldExpired')
          this.$router.replace({name: 'intakeSlots', params: {lang: this.$route.params.lang || 'fa', therapistId: loadIntake().therapistId}})
          return
        }
        return this.createFreshHold(session)
      }).catch((error) => {
        if (this.handleBookingGuard(error)) return
        this.holdReady = false
        this.formStatus = clientMessage('HOLD_RECOVERY_FAILED', this.$route.params.lang || 'fa')
        this.noteStatusError()
      }).finally(() => { this.submitting = false })
    },
    matchesCheckoutContext(receipt, session, isHold = false) {
      if (!receipt || !session.recommendationId || !session.selectedSlotId || !this.person || !this.person.memberId) return false
      if (String(receipt.recommendation_id) !== String(session.recommendationId)
        || String(receipt.slot_id) !== String(session.selectedSlotId)
        || String(receipt.therapist_id) !== String(this.person.id)
        || String(receipt.recommendation_member_id) !== String(this.person.memberId)) return false
      if (!isHold) return true
      const quoteId = session.priceQuoteId || this.person.priceQuoteId
      if (quoteId) return String(receipt.price_quote_id) === String(quoteId)
      // Older browser drafts did not store quote IDs. Only their original validation can resume.
      return !!session.validationId && String(receipt.validation_id) === String(session.validationId)
    },
    rememberBooking(data) {
      this.bookingId = data.booking_id
      if (data.accepted_amount != null) {
        this.acceptedAmount = data.accepted_amount
        this.acceptedCurrency = data.accepted_currency || this.acceptedCurrency
      }
      saveMatchSession({bookingId: data.booking_id, holdId: data.hold_id || this.holdId,
        acceptedAmount: this.acceptedAmount, acceptedCurrency: this.acceptedCurrency})
      saveIntake({paymentMethod: this.paymentMethod, bookingId: data.booking_id})
    },
    async resumeActiveCheckout(session) {
      const active = await getActiveBooking({recommendation_id: session.recommendationId, slot_id: session.selectedSlotId})
      const booking = active && active.booking
      const matchingBooking = this.matchesCheckoutContext(booking, session)
      if (matchingBooking) {
        if (['PAYMENT_VERIFIED', 'CONFIRMING', 'CONFIRMED'].includes(booking.state) || booking.client_status === 'PROCESSING') {
          this.rememberBooking(booking)
          this.holdReady = false
          this.$router.replace({name: 'intakePayProcessing', params: {lang: this.$route.params.lang || 'fa'}, query: {bookingId: booking.booking_id}})
          return true
        }
      }
      // An older unpaid booking may outlive its hold. A newer live exact hold takes precedence.
      if (this.matchesCheckoutContext(active && active.hold, session, true) && holdDeadline(active.hold)
        && (!matchingBooking || active.hold.hold_id !== booking.hold_id)) {
        this.applyHoldPayload(active.hold, session.holdIdempotencyKey)
        return true
      }
      if (matchingBooking) {
        if (booking.state === 'AWAITING_PAYMENT' && booking.hold_id) {
          const hold = active.hold && active.hold.hold_id === booking.hold_id ? active.hold : await getHold(booking.hold_id)
          if (!this.matchesCheckoutContext(hold, session, true)) throw new Error('HOLD_RECOVERY_FAILED')
          this.rememberBooking(booking)
          this.applyHoldPayload(hold, session.holdIdempotencyKey)
          return true
        }
      }
      if (this.matchesCheckoutContext(active && active.hold, session, true)) {
        this.applyHoldPayload(active.hold, session.holdIdempotencyKey)
        return true
      }
      return false
    },
    applyHoldPayload(data, idempotencyKey) {
      const deadline = holdDeadline(data)
      if (!deadline) {
        this.holdReady = false
        this.holdSeconds = 0
        this.goHoldExpired()
        return false
      }
      this.holdId = data.hold_id
      this.holdReady = true
      const session = loadMatchSession() || {}
      const checkoutChanged = session.checkoutIdempotencyHoldId && session.checkoutIdempotencyHoldId !== data.hold_id
      saveMatchSession({
        holdId: data.hold_id,
        holdExpiresAt: data.expires_at,
        holdIdempotencyKey: idempotencyKey || session.holdIdempotencyKey,
        ...(checkoutChanged ? {checkoutIdempotencyKey: null, checkoutIdempotencyHoldId: null} : {}),
      })
      this.holdDeadlineAt = deadline
      this.startHoldCountdown()
      return true
    },
    createFreshHold(session) {
      const idempotencyKey = session.holdIdempotencyKey || newHoldIdempotencyKey()
      saveMatchSession({holdIdempotencyKey: idempotencyKey})
      this.submitting = true
      return holdBooking({validation_id: session.validationId}, {
        idempotencyKey,
      }).then((data) => {
        this.applyHoldPayload(data, idempotencyKey)
      }).catch(async (err) => {
        if (this.handleBookingGuard(err)) return
        const code = (err && err.code) || (err && err.data && err.data.code) || ''
        if (code === 'HOLD_LIMIT_EXCEEDED') {
          // A concurrent checkout may have won the race. Recover only this selection.
          if (await this.resumeActiveCheckout(session)) return
          this.formStatus = clientMessage(code, this.$route.params.lang || 'fa')
          this.noteStatusError()
          return
        }
        this.formStatus = clientMessage('HOLD_RECOVERY_FAILED', this.$route.params.lang || 'fa')
        this.noteStatusError()
        if (code === 'NO_VALID_SLOT' || code === 'SLOT_HELD' || code === 'SLOT_NOT_AVAILABLE') {
          this.$router.replace({
            name: 'intakeSlots',
            params: {
              lang: this.$route.params.lang || 'fa',
              therapistId: loadIntake().therapistId,
            },
          })
          return
        }
      }).finally(() => {
        this.submitting = false
      })
    },
    refreshHoldCountdown() {
      this.holdSeconds = holdRemaining(this.holdDeadlineAt)
      if (this.holdReady && this.holdSeconds <= 0 && this.phase === 'checkout') {
        this.holdReady = false
        this.clearTimers()
        this.formStatus = this.$t('intake.checkoutHoldExpired')
        this.noteStatusError()
        saveMatchSession({holdId: null, holdExpiresAt: null, holdIdempotencyKey: null})
        this.goHoldExpired()
      }
    },
    startHoldCountdown() {
      this.clearTimers()
      this.refreshHoldCountdown()
      if (this.holdReady) this.holdTimer = setInterval(this.refreshHoldCountdown, 1000)
    },
    checkoutRequestKey() {
      const session = loadMatchSession() || {}
      if (session.checkoutIdempotencyHoldId === this.holdId && session.checkoutIdempotencyKey) return session.checkoutIdempotencyKey
      const key = newHoldIdempotencyKey().replace(/^hold_/, 'checkout_')
      saveMatchSession({checkoutIdempotencyKey: key, checkoutIdempotencyHoldId: this.holdId})
      return key
    },
    pay() {
      if (this.submitting || this.bookingGuard) return
      this.refreshHoldCountdown()
      this.formStatus = ''
      this.paymentError = ''
      if (!this.paymentPolicyLoaded || !this.paymentInitiationEnabled) {
        this.formStatus = this.$t('intake.bookingUi.paymentUnavailable')
        this.noteStatusError()
        return
      }
      if (!this.holdReady || this.holdSeconds <= 0) {
        this.formStatus = this.$t('intake.checkoutHoldExpired')
        this.noteStatusError()
        return
      }
      if (!this.paymentMethod) {
        this.paymentError = this.$t('intake.fieldRequired')
        this.formStatus = this.$t('intake.formIncomplete')
        this.noteInvalid()
        return
      }

      this.submitting = true
      saveIntake({paymentMethod: this.paymentMethod})
      this.clearTimers()

      if (!this.useLiveBooking || !this.holdId) {
        this.formStatus = this.$t('intake.bookingUi.paymentUnavailable')
        this.noteStatusError()
        this.submitting = false
        return
      }

      return checkoutHold(this.holdId, {idempotencyKey: this.checkoutRequestKey()}).then((data) => {
        this.rememberBooking(data)
        // Checkout can extend the initial hold. Keep that receipt before any gateway navigation fails.
        const session = loadMatchSession() || {}
        if (data.hold && data.hold.state === 'HELD' && data.hold.hold_id === this.holdId
          && this.matchesCheckoutContext(data.hold, session, true)) {
          if (!this.applyHoldPayload(data.hold, session.holdIdempotencyKey)) return
          this.clearTimers()
        }

        if (data.immediately_confirmed && data.booking_code) {
          this.bookingCode = data.booking_code
          saveIntake({bookingCode: this.bookingCode})
          this.phase = 'success'
          this.submitting = false
          return
        }

        if (['PAYMENT_VERIFIED', 'CONFIRMING', 'CONFIRMED'].includes(data.state) || data.client_status === 'PROCESSING') {
          this.holdReady = false
          this.$router.replace({name: 'intakePayProcessing', params: {lang: this.$route.params.lang || 'fa'}, query: {bookingId: data.booking_id}})
          return
        }

        const payment = data.payment || {}
        const url = payment.url || payment.payment_url || (payment.data && payment.data.url)
        if (redirectToPayment(url)) {
          return
        }

        this.$router.replace({
          name: 'intakePayProcessing',
          params: {lang: this.$route.params.lang || 'fa'},
          query: {bookingId: data.booking_id},
        })
      }).catch((err) => {
        if (this.handleBookingGuard(err)) return
        const code = (err && err.code) || (err && err.data && err.data.code) || ''
        const rateLimited = code === 'RATE_LIMITED' || Number(err && (err.status || (err.response && err.response.status))) === 429
        this.formStatus = rateLimited
          ? clientError(err, this.$route.params.lang || 'fa')
          : clientMessage(code === 'PAYMENT_REDIRECT_INVALID' ? code : 'PAYMENT_RETRY_REQUIRED', this.$route.params.lang || 'fa')
        this.noteStatusError()
        this.submitting = false
        if (code === 'HOLD_EXPIRED' || code === 'SLOT_HOLD_LOST' || code === 'NO_VALID_SLOT') {
          this.holdReady = false
          this.goHoldExpired()
        } else {
          this.startHoldCountdown()
        }
      })
    },
    onPrepare() {
      this.$router.push({
        name: 'intakeConsent',
        params: {lang: this.$route.params.lang || 'fa'},
      })
    },
    goDashboard() {
      this.$router.push({
        name: 'dashboard',
        params: {lang: this.$route.params.lang || 'fa'},
      })
    },
  },
}
</script>
